import React from "react";
import { INFRA, MODULES, ARCH_FIXES } from "./wbsData";

// ──────────────────────────────────────────────────────────────────────────
// Dashboard WBS progress widget.
// Data comes from `wbsData.js`, which is AUTO-GENERATED by
// `scripts/sync-wbs.js` from `WBS.md` (source of truth per guide §2).
// Never edit wbsData.js directly; edit WBS.md and re-run the sync.
// ──────────────────────────────────────────────────────────────────────────

const T2TWbsProgress = () => {
  // Core modules
  const completed  = MODULES.filter((m) => m.status === "complete");
  const inProgress = MODULES.filter((m) => m.status === "in_progress");
  const remaining  = MODULES.filter((m) => m.status === "planned");

  // Architecture fixes
  const archDone    = ARCH_FIXES.filter((a) => a.status === "complete");
  const archInFlight= ARCH_FIXES.filter((a) => a.status === "in_progress");
  const archPending = ARCH_FIXES.filter((a) => a.status === "planned");

  // Combined totals
  const totalDays      = MODULES.reduce((s, m) => s + m.days, 0)
                        + (INFRA ? INFRA.days : 0)
                        + ARCH_FIXES.reduce((s, a) => s + a.days, 0);
  const completedDays  = completed.reduce((s, m) => s + m.days, 0)
                        + (INFRA && INFRA.status === "complete" ? INFRA.days : 0)
                        + archDone.reduce((s, a) => s + a.days, 0);
  const inProgressDays = inProgress.reduce((s, m) => s + m.days, 0)
                        + archInFlight.reduce((s, a) => s + a.days, 0);
  const percent        = totalDays > 0 ? Math.round((completedDays / totalDays) * 1000) / 10 : 0;

  const nextModule = inProgress[0] || remaining[0];

  return (
    <div style={{
      background: "linear-gradient(135deg, #0F4C81 0%, #1B6BAA 40%, #2980B9 70%, #1A5276 100%)",
      borderRadius: 16, padding: "32px 36px", color: "white", position: "relative", overflow: "hidden",
      boxShadow: "0 8px 32px rgba(15, 76, 129, 0.4)",
    }}>
      {/* Background pattern */}
      <div style={{ position: "absolute", inset: 0, opacity: 0.05, backgroundImage: "radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)", backgroundSize: "60px 60px" }} />

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, position: "relative" }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 800, margin: "0 0 4px", letterSpacing: 0.5 }}>OSIF T2T Platform</h2>
          <p style={{ fontSize: 13, margin: 0, opacity: 0.7 }}>Work Breakdown Structure &middot; Overall Build Progress</p>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 42, fontWeight: 900, lineHeight: 1 }}>{percent}%</div>
          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>complete</div>
          <div style={{ fontSize: 11, opacity: 0.5 }}>{completed.length} of {MODULES.length} modules &middot; {archDone.length} of {ARCH_FIXES.length} arch fixes</div>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ marginBottom: 28, position: "relative" }}>
        <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.8, marginBottom: 6 }}>{percent}%</div>
        <div style={{ height: 18, background: "rgba(255,255,255,0.15)", borderRadius: 20, overflow: "hidden", position: "relative" }}>
          <div style={{
            height: "100%", width: `${percent}%`,
            background: "linear-gradient(90deg, #36B82A, #4ADE80)",
            borderRadius: 20, transition: "width 1s ease",
            boxShadow: "0 0 12px rgba(54, 184, 42, 0.4)",
          }} />
          {inProgressDays > 0 && (
            <div style={{
              position: "absolute", top: 0, left: `${percent}%`, height: "100%",
              width: `${(inProgressDays / totalDays) * 100}%`,
              background: "linear-gradient(90deg, #F59E0B, #FBBF24)",
              borderRadius: "0 20px 20px 0",
            }} />
          )}
        </div>
      </div>

      {/* Module cards */}
      <div style={{ display: "flex", gap: 20, position: "relative" }}>
        {/* Completed */}
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
            <span style={{ fontSize: 14 }}>{"\u2705"}</span>
            <span style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, opacity: 0.9 }}>Completed</span>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {completed.map((m) => (
              <div key={m.id} style={{
                padding: "10px 14px", borderRadius: 8,
                background: "linear-gradient(135deg, #36B82A, #2D9B23)",
                minWidth: 110, boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              }}>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5 }}>{m.id}</div>
                <div style={{ fontSize: 10, opacity: 0.85, marginTop: 2 }}>{m.name}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div style={{ width: 1, background: "rgba(255,255,255,0.15)", margin: "0 4px" }} />

        {/* Remaining */}
        <div style={{ flex: 1.5 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
            <span style={{ fontSize: 14 }}>{"\u23F3"}</span>
            <span style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, opacity: 0.9 }}>Remaining</span>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {inProgress.map((m) => (
              <div key={m.id} style={{
                padding: "10px 14px", borderRadius: 8,
                background: "linear-gradient(135deg, #F59E0B, #D97706)",
                minWidth: 110, boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              }}>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5 }}>{m.id}</div>
                <div style={{ fontSize: 10, opacity: 0.85, marginTop: 2 }}>{m.name}</div>
              </div>
            ))}
            {remaining.map((m) => (
              <div key={m.id} style={{
                padding: "10px 14px", borderRadius: 8,
                background: "rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.15)",
                minWidth: 110,
              }}>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, opacity: 0.9 }}>{m.id}</div>
                <div style={{ fontSize: 10, opacity: 0.6, marginTop: 2 }}>{m.name}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Architecture refinements */}
      <div style={{ marginTop: 24, paddingTop: 18, borderTop: "1px solid rgba(255,255,255,0.1)", position: "relative" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
          <span style={{ fontSize: 14 }}>{"\uD83D\uDEE0"}</span>
          <span style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, opacity: 0.9 }}>Architecture Refinements</span>
          <span style={{ fontSize: 10, opacity: 0.6, marginLeft: 4 }}>layering & correctness fixes on top of T2T-03</span>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {archDone.map((a) => (
            <div key={a.id} title={a.detail} style={{
              padding: "10px 14px", borderRadius: 8,
              background: "linear-gradient(135deg, #059669, #047857)",
              minWidth: 160, boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            }}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, display: "flex", alignItems: "center", gap: 6 }}>
                {a.id} <span style={{ fontSize: 9, background: "rgba(255,255,255,0.2)", padding: "1px 5px", borderRadius: 4 }}>DONE</span>
              </div>
              <div style={{ fontSize: 10, opacity: 0.9, marginTop: 2 }}>{a.name}</div>
            </div>
          ))}
          {archInFlight.map((a) => (
            <div key={a.id} title={a.detail} style={{
              padding: "10px 14px", borderRadius: 8,
              background: "linear-gradient(135deg, #F59E0B, #D97706)",
              minWidth: 160, boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            }}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, display: "flex", alignItems: "center", gap: 6 }}>
                {a.id} <span style={{ fontSize: 9, background: "rgba(255,255,255,0.25)", padding: "1px 5px", borderRadius: 4 }}>IN PROGRESS</span>
              </div>
              <div style={{ fontSize: 10, opacity: 0.9, marginTop: 2 }}>{a.name}</div>
            </div>
          ))}
          {archPending.map((a) => (
            <div key={a.id} title={a.detail} style={{
              padding: "10px 14px", borderRadius: 8,
              background: "rgba(255,255,255,0.08)",
              border: "1px dashed rgba(255,255,255,0.2)",
              minWidth: 160,
            }}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, opacity: 0.85 }}>{a.id}</div>
              <div style={{ fontSize: 10, opacity: 0.6, marginTop: 2 }}>{a.name}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div style={{ marginTop: 24, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,0.1)", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11, opacity: 0.6 }}>
        <span>OSIF T2T Platform 2024 &middot; Build in progress &middot; {Math.round(completedDays)} of {Math.round(totalDays)} dev days completed</span>
        {nextModule && <span>Next milestone: {nextModule.id} {nextModule.name}</span>}
      </div>
    </div>
  );
};

export default T2TWbsProgress;
